/* Domain model — shapes come from @nextdone/shared so the webapp and API
 * agree. Helpers below are display-only. Wallet TON is read from chain. */
import type { BadgeTone, ToastTone } from '@nextdone/ui';
import type {
  Deal,
  Job,
  Me,
  Milestone,
  Party,
  Settlement,
  TrustTier,
} from '@nextdone/shared';
import { AUTO_RELEASE_MS } from '@nextdone/shared';

export type {
  Category,
  ChainRead,
  Channel,
  Deal,
  DealEvent,
  DealStatus,
  Job,
  JobStatus,
  Me,
  Milestone,
  MilestoneSpec,
  MilestoneStatus,
  Party,
  Proposal,
  ProposalStatus,
  Role,
  Seller,
  Settlement,
  TrustTier,
} from '@nextdone/shared';

export {
  AUTO_RELEASE_MS,
  CATEGORIES,
  FEE_RATE,
  STAKE_GATE,
  TIER_STEPS,
  netAfterFee,
  nextTier,
  tierFor,
} from '@nextdone/shared';

export const ZERO_SETTLEMENT: Settlement = {
  dealsSettled: 0,
  tonReleased: 0,
  disputeRate: 0,
  distinctCounterparties: 0,
};

export function party(name: string, tier: TrustTier, extra: Partial<Party> = {}): Party {
  return { name, tier, ...ZERO_SETTLEMENT, ...extra };
}

export interface ToastMsg {
  id: number;
  tone: ToastTone;
  text: string;
  detail?: string;
  /** Route to open when tapped. */
  to?: string;
}

export interface State {
  me: Me | null;
  jobs: Job[];
  deals: Deal[];
  toasts: ToastMsg[];
}

export type LoadStatus = 'loading' | 'ready' | 'error' | 'unauthenticated';

/** ms remaining on a delivered milestone's auto-release clock. */
export function autoReleaseLeft(m: Milestone, now: number): number {
  if (m.status !== 'delivered' || !m.deliveredAt) return 0;
  return Math.max(0, m.deliveredAt + AUTO_RELEASE_MS - now);
}

/** "T-71:59:12" for the ledger layer. */
export function fmtCountdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return `T-${h}:${m}:${sec}`;
}

/** Deal summary state for list badges. */
export function dealTone(deal: Deal): { tone: BadgeTone; label: string } {
  if (deal.status === 'disputed') return { tone: 'dispute', label: 'In dispute' };
  if (deal.status === 'settled') return { tone: 'released', label: 'Settled' };
  if (deal.status === 'cancelled') return { tone: 'neutral', label: 'Cancelled' };
  const delivered = deal.milestones.find((m) => m.status === 'delivered');
  if (delivered) return { tone: 'timer', label: 'On the clock' };
  return { tone: 'locked', label: 'Locked' };
}

/** Sum still held — only meaningful when chainRead is `ok`. */
export function lockedAmount(deal: Deal): number {
  return deal.milestones
    .filter((m) => m.status !== 'released' && m.status !== 'refunded')
    .reduce((s, m) => s + m.amount, 0);
}

export function releasedAmount(deal: Deal): number {
  return deal.milestones.filter((m) => m.status === 'released').reduce((s, m) => s + m.amount, 0);
}

/** Money the list may show. `unknown` is not a zero; unattached is not locked. */
export function listAmount(deal: Deal): number | string {
  if (deal.chainRead !== 'ok') return '—';
  if (deal.status === 'settled') return deal.milestones.reduce((s, m) => s + m.amount, 0);
  return lockedAmount(deal);
}

/** "M2 of 3" — the first unsettled step, ledger-style. */
export function dealStep(deal: Deal): string {
  const i = deal.milestones.findIndex((m) => m.status !== 'released' && m.status !== 'refunded');
  const n = deal.milestones.length;
  return i < 0 ? `M${n} of ${n}` : `M${i + 1} of ${n}`;
}

export function fmtAgo(at: number, now: number): string {
  const min = Math.max(1, Math.floor((now - at) / 60000));
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
